import {
  type ApolloClient,
  type NormalizedCacheObject,
  gql,
} from '@apollo/client';
import type {
  CompanyProfile as CompanyProfileFromGraph,
  CompanyNameSlug as CompanyNameSlugFromGraph,
  PostCompanyProfileReviewUpvotesPayload as ReviewUpvotesPayloadFromGraph,
  PostCompanyProfileReviewFlagPayload as ReviewFlagPayloadFromGraph,
} from '@seek/ca-graphql-schema/types';

import type {
  CompanyProfile,
  Jobs,
  Logger,
  Reviews,
  ReviewUpvotes as ReviewUpvotesFromGraph,
  SortOrder,
} from '@seek/libs-shared';
import { createLegacyCompanyProfileService } from '../legacyCompanyProfileService/legacyCompanyProfileService';
import { createSearchService } from '../searchService/searchService';
import {
  mapCompanyProfileModel,
  mapReviewsModel,
  mapJobsModel,
  mapSearchCompanyModel,
  mapCompanyProfileModelFromDraft,
} from './mapper';
import type { AppConfig } from '../../config';
import type { SearchCompaniesResult } from './types';

export interface CompanyProfileService {
  searchCompanies: ({
    searchTerm,
  }: {
    searchTerm: string;
  }) => Promise<SearchCompaniesResult>;
  getCompanyProfile: (
    companyId: string,
    isDraft?: boolean,
  ) => Promise<CompanyProfile>;
  getCompanyProfileForeignId: (companyId: string) => Promise<string>;
  getJobs: (companyId: string, page?: number) => Promise<Jobs>;
  getReviews: (
    companyId: string,
    page?: number,
    sortOrder?: SortOrder,
  ) => Promise<Reviews>;
  getCompanyNameSlug: (
    companySlug: string,
    isLegacyUrl: boolean,
  ) => Promise<CompanyNameSlugFromGraph>;
  getUpvoteIds: ({
    companyId,
  }: {
    companyId: string;
  }) => Promise<ReviewUpvotesFromGraph>;
  postUpvoteReview: ({
    companyId,
    reviewId,
  }: {
    companyId: string;
    reviewId: string;
  }) => Promise<ReviewUpvotesPayloadFromGraph | undefined>;
  postFlagReview: ({
    companyId,
    reviewId,
    reason,
    details,
  }: {
    companyId: string;
    reviewId: string;
    reason: string;
    details: string;
  }) => Promise<ReviewFlagPayloadFromGraph | undefined>;
}

export const createCompanyProfileService = ({
  config,
  client,
  logger,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  config: AppConfig;
  logger: Logger;
}): CompanyProfileService => {
  const legacyCompanyProfileService = createLegacyCompanyProfileService({
    companyProfilesApiBaseUrl: config.companyProfilesApiBaseUrl,
    config,
    logger,
  });

  const searchService = createSearchService({
    config,
    logger,
  });

  const searchCompanies = async ({
    searchTerm,
  }: {
    searchTerm: string;
  }): Promise<SearchCompaniesResult> => {
    try {
      const res = await legacyCompanyProfileService.searchCompanies({
        searchTerm,
      });

      if (!res.data) {
        return { companies: [], totalCompanies: 0 };
      }

      return mapSearchCompanyModel(res);
    } catch (e) {
      throw new Error(`Error from searchCompanies: ${e}`);
    }
  };

  const getCompanyProfileForeignId = async (
    companyId: string,
  ): Promise<string> =>
    await legacyCompanyProfileService.getCompanyProfileForeignId(companyId);

  const getJobs = async (companyId: string, page = 1): Promise<Jobs> => {
    const organisationId = await getCompanyProfileForeignId(companyId);

    if (!organisationId) return { jobs: [], totalJobs: 0 };

    const jobs = await searchService.getJobsList({
      organisationId,
      page,
      country: config.country,
    });

    return mapJobsModel(jobs);
  };

  const getCompanyProfile = async (
    companyId: string,
    isDraft = false,
  ): Promise<CompanyProfile> => {
    if (isDraft) {
      const draft =
        await legacyCompanyProfileService.getLegacyCompanyProfilePreview(
          companyId,
        );

      if (!draft) {
        throw new Error(`Company not found: ${companyId}`);
      }

      const legacyCompanyReviewsSummary =
        await legacyCompanyProfileService.getCompanyReviewsSummary(companyId);

      return mapCompanyProfileModelFromDraft(
        draft,
        legacyCompanyReviewsSummary,
      );
    }

    // only foreignCompanyId and gallery are used from legacyCompanyProfile
    const legacyCompanyProfile =
      await legacyCompanyProfileService.getLegacyCompanyProfile(companyId);

    if (!legacyCompanyProfile)
      throw new Error(`Company not found: ${companyId}`);

    const legacyCompanyReviewsSummary =
      await legacyCompanyProfileService.getCompanyReviewsSummary(companyId);

    const response = await client.query<{
      companyProfile: CompanyProfileFromGraph;
    }>({
      query: GET_COMPANY_PROFILE_QUERY,
      variables: {
        companyProfileId: companyId,
        zone: config.zone,
        locale: config.locale,
      },
    });

    return mapCompanyProfileModel({
      companyProfileFromGraph: response.data?.companyProfile,
      legacyCompanyReviewsSummary,
      legacyCompanyProfile,
    });
  };

  // ------- Company Reviews -------
  const GET_REVIEWS_QUERY = gql`
    query CompanyReviews($companyProfileId: ID!, $zone: Zone!, $page: Int) {
      companyProfile(id: $companyProfileId, zone: $zone) {
        branding {
          logo
        }
        reviews(page: $page) {
          data {
            companyId
            pros
            cons
            jobTitle
            title
            id
            workLocation
            upvotes {
              count
              description
            }
            salarySummary
            employmentStatus
            recommended {
              description
              value
            }
            ratings {
              rating
              description
            }
            overallRating
            createdAt {
              shortAbsoluteLabel
            }
          }
          paging {
            page
            perPage
            total
            totalPages
          }
        }
        reviewsSummary {
          overallRating {
            value
            numberOfReviews {
              value
            }
          }
        }
      }
    }
  `;

  const getShouldDisplayReviews = async (companyId: string): Promise<boolean> =>
    await legacyCompanyProfileService.getShouldDisplayReviews(companyId);

  const getReviews = async (
    companyId: string,
    page = 1,
    sortOrder: SortOrder = 'most recent',
  ): Promise<Reviews> => {
    const response = await client.query<{
      companyProfile: CompanyProfileFromGraph;
    }>({
      query: GET_REVIEWS_QUERY,
      variables: {
        companyProfileId: companyId,
        zone: config.zone,
        locale: config.locale,
        page,
      },
    });

    const shouldDisplayReviews = await getShouldDisplayReviews(companyId);

    // Used purely to get the replies for the reviews. This is a temporary solution.
    // TODO: Remove this once replies is implemented in Candy Graph.
    const legacyReviews = await legacyCompanyProfileService.getCompanyReviews(
      companyId,
      {
        page,
        sort: sortOrder === 'most helpful' ? '-upvote_count' : '',
      },
    );

    return mapReviewsModel(
      response.data?.companyProfile,
      shouldDisplayReviews,
      legacyReviews,
      sortOrder,
      config.language,
    );
  };

  const getCompanyNameSlug = async (
    companySlug: string,
    isLegacyUrl: boolean,
  ): Promise<CompanyNameSlugFromGraph> => {
    const response = await client.query<{
      companyNameSlug: CompanyNameSlugFromGraph;
    }>({
      query: GET_COMPANY_NAME_SLUG_QUERY,
      variables: {
        companySlug,
        zone: config.zone,
        isLegacyUrl,
      },
    });

    return response?.data?.companyNameSlug;
  };

  const getUpvoteIds = async ({
    companyId,
  }: {
    companyId: string;
  }): Promise<ReviewUpvotesFromGraph> => {
    const response = await client.query<{
      companyProfileReviewUpvotes: ReviewUpvotesFromGraph;
    }>({
      query: GET_COMPANY_REVIEW_UPVOTES_QUERY,
      variables: {
        companyProfileReviewUpvotesId: companyId,
        zone: config.zone,
      },
    });

    return response?.data?.companyProfileReviewUpvotes;
  };

  const postUpvoteReview = async ({
    companyId,
    reviewId,
  }: {
    companyId: string;
    reviewId: string;
  }): Promise<ReviewUpvotesPayloadFromGraph | undefined> => {
    const response = await client.mutate<{
      postCompanyProfileReviewUpvotes: ReviewUpvotesPayloadFromGraph;
    }>({
      mutation: POST_COMPANY_REVIEW_UPVOTE_MUTATE,
      variables: {
        input: {
          companyId,
          reviewId,
        },
      },
    });

    return response?.data?.postCompanyProfileReviewUpvotes;
  };

  const postFlagReview = async ({
    companyId,
    reviewId,
    reason,
    details,
  }: {
    companyId: string;
    reviewId: string;
    reason: string;
    details: string;
  }): Promise<ReviewFlagPayloadFromGraph | undefined> => {
    const response = await client.mutate<{
      postCompanyProfileReviewFlag: ReviewFlagPayloadFromGraph;
    }>({
      mutation: POST_COMPANY_REVIEW_FLAG_MUTATE,
      variables: {
        input: {
          companyId,
          reviewId,
          reason,
          details,
        },
      },
    });

    return response?.data?.postCompanyProfileReviewFlag;
  };

  return {
    getCompanyNameSlug,
    getCompanyProfile,
    getCompanyProfileForeignId,
    getJobs,
    getReviews,
    getUpvoteIds,
    postUpvoteReview,
    postFlagReview,
    searchCompanies,
  };
};

const GET_COMPANY_PROFILE_QUERY = gql`
  query CompanyProfile($companyProfileId: ID!, $zone: Zone!, $locale: Locale!) {
    companyProfile(id: $companyProfileId, zone: $zone) {
      overview {
        description {
          full
          paragraphs
        }
        industry
        primaryLocation
        size {
          description
        }
        specialities
        website {
          url
          displayName
        }
      }
      awards {
        imageUrl
        description
        year
      }
      branding {
        logo
        cover {
          aspectRatio
          url
        }
      }
      cultureAndValues {
        values {
          title
          description
        }
        title
        imageUrl
        description
      }
      customStatement {
        title
        description {
          full
          paragraphs
        }
      }
      featured {
        title
        videoUrl
        imageUrl
        description {
          full
          paragraphs
        }
      }
      reviews {
        paging {
          totalPages
          total
          perPage
          page
        }
        data {
          companyId
          cons
          createdAt {
            longAbsoluteLabel
            dateTimeUtc
            dateTime
            longLabel
            shortAbsoluteLabel
            shortLabel
          }
          id
          employmentStatus
          jobTitle
          overallRating
          pros
          ratings {
            rating
            description
          }
          recommended {
            description
            value
          }
          salarySummary
          title
          upvotes {
            description
            count
          }
          workLocation
        }
      }
      isBeta
      id
      isCompanyProfile
      missionStatement {
        description {
          paragraphs
          full
        }
      }
      name
      perksAndBenefits {
        title
        description
      }
      reviewsSummary {
        salaryRating {
          value
          rating
          description
        }
        ratings {
          rating
          description
        }
        overallRating {
          numberOfReviews {
            value
            description(locale: $locale)
          }
          value
        }
        employeeRecommendation {
          description
          rating
          value
        }
        communityGuideline {
          url
        }
      }
      tracking {
        isCompanyProfile
      }
    }
  }
`;
const GET_COMPANY_NAME_SLUG_QUERY = gql`
  query CompanyNameSlug(
    $companySlug: String!
    $zone: Zone!
    $isLegacyUrl: Boolean!
  ) {
    companyNameSlug(
      companySlug: $companySlug
      zone: $zone
      isLegacyUrl: $isLegacyUrl
    ) {
      companySlug
      status
    }
  }
`;

const GET_COMPANY_REVIEW_UPVOTES_QUERY = gql`
  query CompanyProfileReviewUpvotes(
    $companyProfileReviewUpvotesId: ID!
    $zone: Zone!
  ) {
    companyProfileReviewUpvotes(
      id: $companyProfileReviewUpvotesId
      zone: $zone
    ) {
      upvotes
    }
  }
`;

const POST_COMPANY_REVIEW_UPVOTE_MUTATE = gql`
  mutation PostCompanyProfileReviewUpvotes(
    $input: PostCompanyProfileReviewUpvotesInput!
  ) {
    postCompanyProfileReviewUpvotes(input: $input) {
      success
    }
  }
`;

const POST_COMPANY_REVIEW_FLAG_MUTATE = gql`
  mutation PostCompanyProfileReviewFlag(
    $input: PostCompanyProfileReviewFlagInput!
  ) {
    postCompanyProfileReviewFlag(input: $input) {
      success
    }
  }
`;
